import {setTelemetryUser} from '../telemetry';
/**
 * 登录态:手机号验证码登录(新号自动注册)、手机号密码登录、微信扫码登录,
 * 与现有官网同一套账号服务与同一套令牌存储,同域打开个人中心时登录态直接沿用。
 */
import {createContext,useCallback,useContext,useEffect,useMemo,useRef,useState,type ReactNode} from 'react';
import {ApiError,apiFetch,assertSessionCurrent,isAbortError,refreshSession,setOnSessionExpired,tokenStore,type ApiUser,type LoginResponse,type OkResponse} from './api';

/** 首版仅支持中国大陆手机号。 */
export function isValidChinaMobile(phone:string):boolean{return /^1[3-9]\d{9}$/.test(phone)}

/** 138****8000;异常长度时保底遮盖中段。 */
export function maskPhone(phone:string):string{
 if(/^1\d{10}$/.test(phone))return `${phone.slice(0,3)}****${phone.slice(7)}`;
 if(phone.length>4)return `${phone.slice(0,2)}****${phone.slice(-2)}`;
 return '****';
}

export function sendSmsCode(phone:string,signal?:AbortSignal):Promise<OkResponse>{
 return apiFetch<OkResponse>('/v1/auth/sms/code',{body:{phone},signal});
}

export type SessionStatus='checking'|'authenticated'|'anonymous'|'unavailable';

export interface SessionContextValue{
 status:SessionStatus;
 version:number;
 retrySession():void;
 user:ApiUser|null;
 sendCode(phone:string,signal?:AbortSignal):Promise<void>;
 loginWithCode(phone:string,code:string,signal?:AbortSignal):Promise<LoginResponse>;
 loginWithPassword(phone:string,password:string,signal?:AbortSignal):Promise<LoginResponse>;
 /** 微信会话兑换成功后同步登录态。 */
 acceptLogin(result:LoginResponse,expectedVersion:number):void;
 updateUser(next:Partial<ApiUser>&Pick<ApiUser,'id'>):void;
 logout():Promise<void>;
}

const SessionContext=createContext<SessionContextValue|null>(null);

/** Each visit renews the server session; transient renewal failures can still
 * restore the user with a usable access token. No local timestamp grants access. */
async function restoreSession(renew:boolean):Promise<ApiUser|null>{
 if(!tokenStore.hasTokens())return null;
 const version=tokenStore.getSessionVersion();
 if(renew){
  try{await refreshSession(version)}
  catch(error){
   if(isAbortError(error))throw error;
   if(!tokenStore.hasTokens())return null;
   assertSessionCurrent(version);
  }
 }
 try{return await apiFetch<ApiUser>('/v1/user/me',{auth:true})}
 catch(error){
  // 401 由客户端自动刷新过;其他失败(网络/5xx)不清令牌,后续操作仍可恢复
  if(error instanceof ApiError&&error.status===401)return null;
  throw error;
 }
}

export function SessionProvider({children}:{children:ReactNode}){
 const [status,setStatus]=useState<SessionStatus>('checking');
 const [user,setUser]=useState<ApiUser|null>(null);
 const [version,setVersion]=useState(()=>tokenStore.getSessionVersion());
 const [restoreKey,setRestoreKey]=useState(0);
 const renewOnRestore=useRef(true);
 const lastResumeAttempt=useRef(0);
 const retrySession=useCallback(()=>{renewOnRestore.current=true;setStatus('checking');setRestoreKey(v=>v+1)},[]);
 useEffect(()=>{if(status!=='checking'&&status!=='unavailable')setTelemetryUser(user?.id??null)},[status,user?.id]);

 // 刷新令牌彻底失败时由 API 客户端通知:清 UI 登录态
 useEffect(()=>{
  setOnSessionExpired(()=>{setUser(null);setVersion(tokenStore.getSessionVersion());setStatus('anonymous')});
  const unsubscribe=tokenStore.subscribe(()=>{
   setUser(null);
   setVersion(tokenStore.getSessionVersion());
   if(!tokenStore.hasTokens()){setStatus('anonymous');return}
   // A newly installed session in another tab is already fresh. Restore its
   // identity without rotating it again or retaining the previous user's data.
   renewOnRestore.current=false;
   setStatus('checking');setRestoreKey(v=>v+1);
  });
  return()=>{setOnSessionExpired(null);unsubscribe()};
 },[]);

 useEffect(()=>{
  let cancelled=false;
  const version=tokenStore.getSessionVersion();
  void restoreSession(renewOnRestore.current).then(restored=>{
   if(cancelled||version!==tokenStore.getSessionVersion())return;
   setUser(restored);
   setVersion(version);
   setStatus(restored?'authenticated':'anonymous');
  }).catch(()=>{if(!cancelled&&version===tokenStore.getSessionVersion()){setUser(null);setStatus('unavailable')}});
  return()=>{cancelled=true};
 },[restoreKey]);

 useEffect(()=>{
  const resume=()=>{
   const version=tokenStore.getSessionVersion();
   if(document.visibilityState!=='visible'||!tokenStore.hasTokens())return;
   const now=Date.now();
   if(now-lastResumeAttempt.current<60_000||now-tokenStore.getRenewedAt()<60_000)return;
   if(status==='unavailable'){lastResumeAttempt.current=now;retrySession();return}
   if(status!=='authenticated')return;
   lastResumeAttempt.current=now;
   // Credential rejection notifies the provider through setOnSessionExpired;
   // an offline/background renewal failure preserves the currently visible user.
   void refreshSession(version).catch(()=>{});
  };
  const onPageShow=(event:PageTransitionEvent)=>{if(event.persisted)resume()};
  document.addEventListener('visibilitychange',resume);
  window.addEventListener('pageshow',onPageShow);
  window.addEventListener('online',resume);
  return()=>{document.removeEventListener('visibilitychange',resume);window.removeEventListener('pageshow',onPageShow);window.removeEventListener('online',resume)};
 },[status,retrySession]);

 const acceptLogin=useCallback((result:LoginResponse,expectedVersion:number)=>{
  assertSessionCurrent(expectedVersion);
  tokenStore.setTokens(result.access_token,result.refresh_token);
  setVersion(tokenStore.getSessionVersion());
  setUser(result.user);setStatus('authenticated');
 },[]);
 const updateUser=useCallback((next:Partial<ApiUser>&Pick<ApiUser,'id'>)=>{setUser(current=>current?.id===next.id?{...current,...next}:current)},[]);
 const login=useCallback(async(path:string,body:Record<string,string>,signal?:AbortSignal)=>{
  const version=tokenStore.getSessionVersion();
  const result=await apiFetch<LoginResponse>(path,{body,signal});
  signal?.throwIfAborted();
  acceptLogin(result,version);
  return result;
 },[acceptLogin]);

 const value=useMemo<SessionContextValue>(()=>({
  status,
  version,
  user,
  retrySession,
  sendCode:async(phone:string,signal?:AbortSignal)=>{await sendSmsCode(phone,signal)},
  loginWithCode:(phone:string,code:string,signal?:AbortSignal)=>login('/v1/auth/sms/verify',{phone,code},signal),
  loginWithPassword:(phone:string,password:string,signal?:AbortSignal)=>login('/v1/auth/password/login',{phone,password},signal),
  acceptLogin,
  updateUser,
  // 后端登出失败不阻塞本地清理:用户确认退出后登录态必须消失
  logout:async()=>{
   const refresh=tokenStore.getRefreshToken();
   const access=tokenStore.getAccessToken();
   // 先退出本地。吊销只使用旧凭证，不刷新，也不在返回时清理后来登录的会话。
   tokenStore.clear();setUser(null);setVersion(tokenStore.getSessionVersion());setStatus('anonymous');
   try{if(refresh)await apiFetch<OkResponse>('/v1/auth/logout',{body:{refresh},headers:access?{Authorization:`Bearer ${access}`}:{},timeoutMs:5000})}
   catch{/* 忽略 */}
  },
 }),[status,version,user,acceptLogin,updateUser,login,retrySession]);

 return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession():SessionContextValue{
 const context=useContext(SessionContext);
 if(!context)throw new Error('useSession 必须在 SessionProvider 内使用');
 return context;
}

/** 登录后展示用的称呼:昵称优先,其次脱敏手机号。 */
export function displayName(user:ApiUser|null):string{
 if(!user)return '';
 return user.nick_name?.trim()||(user.phone?maskPhone(user.phone):'个人中心');
}
